import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	Optional,
	Output
} from '@angular/core';
import { IconComponent, TypographyComponent } from 'components';
import {
	PreviewSlide,
	ReportDateRange,
	SlideType,
	TitleWidget
} from '../../../../models/preview/preview.models';
import { ReportContentItem } from '../../../../services/report-settings.service';
import {
	applyContentItemOverrides,
	REPORT_DEFAULT_ACCENT_COLOR
} from '../../../../services/report-preview-utils';
import { ReportLanguageService } from '../../../../services/report-language.service';
import { sectionReads } from '../../../../services/report-source';
import { thinSectionMessage } from '../../../../services/report-thin-data';
import { SlideNoDataComponent } from '../slide-no-data/slide-no-data.component';
import { ReportSlideBrandComponent } from '../../components/report-slide-brand/report-slide-brand.component';

/**
 * A volume-sensitive section, for a period with too few mentions to read it.
 *
 * Drawn by the slide host in place of the section's own slide (see
 * report-thin-data.ts for which sections and why). It keeps the section's
 * frame — its title, its date, its footer — so the reader can see WHAT is
 * missing, and says how many mentions there were instead of drawing a chart
 * out of them.
 *
 * Not the section's description, deliberately: that is prose written about
 * this same handful of mentions, and printing "negative sentiment dominated"
 * above "too few to read sentiment reliably" would contradict itself.
 */
@Component({
	standalone: true,
	selector: 'app-thin-section-slide',
	templateUrl: './thin-section-slide.component.html',
	styleUrls: ['./thin-section-slide.component.scss'],
	imports: [
		IconComponent,
		TypographyComponent,
		SlideNoDataComponent,
		ReportSlideBrandComponent
	]
})
export class ThinSectionSlideComponent implements OnChanges {
	@Input() item!: ReportContentItem;
	@Input() brandId!: string;
	@Input() dateRange!: ReportDateRange;
	@Input() themeColor: string | null = null;
	/** The report's mention total: known, and under the floor, or this
	 *  slide would not have been drawn. */
	@Input() mentionTotal = 0;
	// CONTRACT: set by the slide host for a section drawn about the report's
	// source — the backend's own verdict on it, too thin for an entity or one
	// that cannot compare several. Null says the thin period's message.
	@Input() note: string | null = null;
	@Input() editorMode = false;
	@Output() slideBuilt = new EventEmitter<PreviewSlide>();

	title = '';
	dateLabel = '';
	message = '';
	tag = '';
	titleColor = REPORT_DEFAULT_ACCENT_COLOR;

	constructor(
		@Optional() private reportLanguage: ReportLanguageService | null
	) {}

	ngOnChanges(): void {
		if (!this.item || !this.dateRange) return;

		const slide = applyContentItemOverrides(
			this.buildSlide(),
			this.item,
			this.themeColor
		);
		const hidden = new Set(slide.overrides?.hiddenWidgetIds ?? []);
		const titleWidget = this.findTitleWidget(slide, 'title', hidden);
		const dateWidget = this.findTitleWidget(slide, 'date_range', hidden);
		const name = this.item.name || this.item.id;

		this.title = titleWidget?.content.text ?? slide.title;
		this.titleColor =
			titleWidget?.content.color ?? REPORT_DEFAULT_ACCENT_COLOR;
		this.dateLabel = dateWidget?.content.text ?? this.dateRange.label;
		this.tag = name.toLowerCase();
		const language = this.reportLanguage?.language;
		this.message =
			this.note ??
			thinSectionMessage(
				this.mentionTotal,
				sectionReads(this.item.id, name, language),
				this.dateRange,
				language
			);
		this.slideBuilt.emit(slide);
	}

	/**
	 * The same widget ids the section's own slide uses, so a title the
	 * reader rewrote there — saved as an override on that id — is the title
	 * here too, and comes back with the chart once the section fills in.
	 * The message is not a widget: it is a statement of fact, not copy for
	 * the AI editor to reword.
	 */
	private buildSlide(): PreviewSlide {
		const prefix = `${this.item.id}_${this.brandId}`;
		const title = this.item.title || this.item.name;
		return {
			id: `${prefix}_slide`,
			slideType: this.item.id as SlideType,
			title,
			description: this.item.description || '',
			dateRange: this.dateRange,
			widgets: [
				{
					id: `${prefix}_title`,
					kind: 'title',
					visible: true,
					content: { text: title, color: REPORT_DEFAULT_ACCENT_COLOR }
				},
				{
					id: `${prefix}_date`,
					kind: 'date_range',
					visible: true,
					content: { text: this.dateRange.label }
				}
			]
		};
	}

	private findTitleWidget(
		slide: PreviewSlide,
		kind: TitleWidget['kind'],
		hidden: Set<string>
	): TitleWidget | undefined {
		return slide.widgets.find(
			widget => widget.kind === kind && !hidden.has(widget.id)
		) as TitleWidget | undefined;
	}
}
